/**
 * User service for managing user documents in Firestore
 * Handles subscription-related user data
 * 
 * NOTE: For new signups/logins, use syncUserDocument from userManagement/userSync.js
 * This function is kept for backward compatibility
 */

import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { syncUserDocument } from './userManagement/userSync';

const USERS_COLLECTION = 'users';

/**
 * In-memory cache for user documents
 * Cache TTL: 10 seconds (prevents excessive Firestore reads while keeping data fresh)
 */
const userDocumentCache = new Map();
const CACHE_TTL = 10 * 1000; // 10 seconds

/**
 * Cache entry structure: { data: userDoc, timestamp: number }
 */

/**
 * Default subscription values for new users
 */
const DEFAULT_SUBSCRIPTION = {
  plan: 'free',
  stripeCustomerId: null,
  stripeSubscriptionId: null,
  subscriptionStatus: null,
};

/**
 * Gets cached user document if still valid
 * @param {string} uid - User ID
 * @returns {Object|null} Cached user document or null if expired/not found
 */
function getCachedUserDocument(uid) {
  const cached = userDocumentCache.get(uid);
  if (!cached) return null;
  
  const age = Date.now() - cached.timestamp;
  if (age > CACHE_TTL) {
    // Cache expired
    userDocumentCache.delete(uid);
    return null;
  }
  
  return cached.data;
}

/**
 * Sets cached user document
 * @param {string} uid - User ID
 * @param {Object} userDoc - User document data
 */
function setCachedUserDocument(uid, userDoc) {
  userDocumentCache.set(uid, {
    data: userDoc,
    timestamp: Date.now(),
  });
}

/**
 * Invalidates cached user document (call after updates)
 * @param {string} uid - User ID
 */
export function invalidateUserCache(uid) {
  if (uid) {
    userDocumentCache.delete(uid);
  } else {
    // Clear all cache
    userDocumentCache.clear();
  }
}

/**
 * Creates a new user document in Firestore
 * Sets plan to "free" by default, role to "user"
 * 
 * @deprecated Use syncUserDocument from userManagement/userSync.js instead
 * This function is kept for backward compatibility
 * 
 * @param {Object} userData - User data for document creation
 * @param {string} userData.uid - User ID (required)
 * @param {string} userData.email - User email (required)
 * @param {string|null} [userData.displayName] - Display name (optional)
 * @param {string} [userData.plan] - Subscription plan (defaults to 'free')
 * @param {string|null} [userData.stripeCustomerId] - Stripe customer ID (optional)
 * @param {string|null} [userData.stripeSubscriptionId] - Stripe subscription ID (optional)
 * @param {string|null} [userData.subscriptionStatus] - Subscription status (optional)
 * @returns {Promise<void>}
 */
export async function createUserDocument(userData) {
  if (!userData.uid) {
    throw new Error('User ID is required');
  }
  if (!userData.email) {
    throw new Error('Email is required');
  }

  try {
    // Use the new sync service if we have an auth user object
    // Otherwise, create manually for backward compatibility
    if (userData.authUser) {
      return await syncUserDocument(userData.authUser);
    }

    const userRef = doc(db, USERS_COLLECTION, userData.uid);
    
    // Check if user document already exists
    const existingDoc = await getDoc(userRef);
    if (existingDoc.exists()) {
      console.warn(`User document already exists for uid: ${userData.uid}`);
      return; // Don't overwrite existing user
    }

    const userDocument = {
      uid: userData.uid,
      email: userData.email,
      provider: userData.provider || 'password',
      role: userData.role || 'user',
      displayName: userData.displayName || null,
      plan: userData.plan || DEFAULT_SUBSCRIPTION.plan,
      stripeCustomerId: userData.stripeCustomerId ?? DEFAULT_SUBSCRIPTION.stripeCustomerId,
      stripeSubscriptionId: userData.stripeSubscriptionId ?? DEFAULT_SUBSCRIPTION.stripeSubscriptionId,
      subscriptionStatus: userData.subscriptionStatus ?? DEFAULT_SUBSCRIPTION.subscriptionStatus,
      subscriptionStartDate: userData.subscriptionStartDate || null,
      subscriptionEndDate: userData.subscriptionEndDate || null,
      subscriptionCancelAtPeriodEnd: userData.subscriptionCancelAtPeriodEnd || false,
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(userRef, userDocument);
    console.log(`Ô£à User document created for uid: ${userData.uid}`);
  } catch (error) {
    console.error('Error creating user document:', error);
    throw error;
  }
}

/**
 * Gets a user document from Firestore (with caching)
 * @param {string} uid - User ID
 * @param {boolean} forceRefresh - Force refresh from Firestore (skip cache)
 * @returns {Promise<Object|null>} User document or null if not found
 */
export async function getUserDocument(uid, forceRefresh = false) {
  if (!uid) {
    throw new Error('User ID is required');
  }

  // Check cache first (unless force refresh)
  if (!forceRefresh) {
    const cached = getCachedUserDocument(uid);
    if (cached) {
      // Return cached data immediately (no Firestore read)
      return cached;
    }
  }

  // Cache miss - fetch from Firestore
  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      return null;
    }

    const data = userSnap.data();
    
    // Convert Firestore timestamps to Date objects
    const convertTimestamp = (ts) => {
      if (!ts) return null;
      if (ts instanceof Timestamp) {
        return ts.toDate();
      }
      if (ts.toDate && typeof ts.toDate === 'function') {
        return ts.toDate();
      }
      if (ts instanceof Date) {
        return ts;
      }
      return null;
    };

    const userDoc = {
      uid: data.uid,
      email: data.email,
      provider: data.provider || 'password',
      role: data.role || 'user',
      displayName: data.displayName || null,
      plan: data.plan || 'free',
      stripeCustomerId: data.stripeCustomerId || null,
      stripeSubscriptionId: data.stripeSubscriptionId || null,
      subscriptionStatus: data.subscriptionStatus || null,
      subscriptionStartDate: convertTimestamp(data.subscriptionStartDate),
      subscriptionEndDate: convertTimestamp(data.subscriptionEndDate),
      subscriptionCancelAtPeriodEnd: data.subscriptionCancelAtPeriodEnd || false,
      createdAt: convertTimestamp(data.createdAt) || new Date(),
      lastLoginAt: convertTimestamp(data.lastLoginAt),
      updatedAt: convertTimestamp(data.updatedAt) || new Date(),
    };

    // Cache the document for next time
    setCachedUserDocument(uid, userDoc);
    
    return userDoc;
  } catch (error) {
    console.error('Error getting user document:', error);
    throw error;
  }
}

/**
 * Updates a user document in Firestore
 * @param {string} uid - User ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<void>}
 */
export async function updateUserDocument(uid, updateData) {
  if (!uid) {
    throw new Error('User ID is required');
  }

  try {
    const userRef = doc(db, USERS_COLLECTION, uid);
    
    // Verify document exists
    const userSnap = await getDoc(userRef);
    if (!userSnap.exists()) {
      throw new Error(`User document not found for uid: ${uid}`);
    }

    const updatePayload = {
      ...updateData,
      updatedAt: serverTimestamp(),
    };

    // Remove undefined values
    Object.keys(updatePayload).forEach((key) => {
      if (updatePayload[key] === undefined) {
        delete updatePayload[key];
      }
    });

    await updateDoc(userRef, updatePayload);
    
    // Invalidate cache after update
    invalidateUserCache(uid);
    
    console.log(`Ô£à User document updated for uid: ${uid}`);
  } catch (error) {
    console.error('Error updating user document:', error);
    throw error;
  }
}

/**
 * Updates user subscription information
 * @param {string} uid - User ID
 * @param {Object} subscriptionData - Subscription data to update
 * @returns {Promise<void>}
 */
export async function updateUserSubscription(uid, subscriptionData) {
  return updateUserDocument(uid, subscriptionData);
}

/**
 * Gets user's current plan (with caching)
 * @param {string} uid - User ID
 * @returns {Promise<string>} User's plan ('free', 'pro', or 'growth')
 */
export async function getUserPlan(uid) {
  const userDoc = await getUserDocument(uid);
  if (!userDoc) {
    // Return default plan if user document doesn't exist
    return 'free';
  }
  return userDoc.plan || 'free';
}

/**
 * Checks if user has an active subscription
 * @param {string} uid - User ID
 * @returns {Promise<boolean>} True if user has active subscription
 */
export async function hasActiveSubscription(uid) {
  const userDoc = await getUserDocument(uid);
  if (!userDoc) {
    return false;
  }
  return userDoc.subscriptionStatus === 'active' && userDoc.plan !== 'free';
}


